import PrimeVueSelect from 'primevue/select';
import { createFieldFocusBoundary } from './fieldFocusBoundary.js';
import { createTouchPickerOverlayCoordinator } from './touchPickerOverlay.js';
import { alignPickerOverlay } from './pickerOverlayLayout.js';

// Both select wrappers use the real PrimeVue control, including its filter,
// keyboard navigation and slots. A remote result set is already filtered by
// its owner and must not be searched again against only its display labels.
export default {
  name: PrimeVueSelect.name,
  extends: PrimeVueSelect,
  emits: ['clear', 'option-select', 'keydown'],
  props: {
    externalFilter: { type: Boolean, default: false },
    focusBoundaryActive: { type: Boolean, default: false },
    focusBoundaryElements: { type: Function, default: null },
    keepOpenOnSelect: { type: Function, default: null },
    singleResultTabHandler: { type: Function, default: null },
    shiftTabSelect: { type: Function, default: null },
    tabExitHandler: { type: Function, default: null },
    enterQueryHandler: { type: Function, default: null },
  },
  data() {
    return {
      lastSelectedOption: undefined,
      dialogBodyOverlayScrollCoordinator: createTouchPickerOverlayCoordinator(),
      fieldFocusBoundary: createFieldFocusBoundary({
        elements: () => [this.$el, this.overlay, ...(this.focusBoundaryElements?.() || [])],
        active: () => this.focusBoundaryActive,
        onLeave: (event, isCurrent) => this.commitFormFieldBlur(event, isCurrent),
      }),
    };
  },
  computed: {
    visibleOptions() {
      if (!this.externalFilter) return PrimeVueSelect.computed.visibleOptions.call(this);
      return this.optionGroupLabel ? this.flatOptions(this.options) : this.options || [];
    },
    currentSelection() {
      const options = this.optionGroupLabel ? this.flatOptions(this.options) : this.options || [];
      return options.find((option) => !this.isOptionGroup(option) && this.isSelected(option));
    },
    selectedOption() {
      if (this.currentSelection !== undefined) return this.currentSelection;
      if (this.lastSelectedOption !== undefined && this.isSelected(this.lastSelectedOption)) {
        return this.lastSelectedOption;
      }
      return !this.optionValue && this.d_value !== null && this.d_value !== undefined
        ? this.d_value
        : undefined;
    },
    label() {
      return this.selectedOption !== undefined
        ? (this.getOptionLabel(this.selectedOption) ?? this.selectedOption)
        : this.placeholder || 'p-emptylabel';
    },
  },
  watch: {
    currentSelection: {
      immediate: true,
      handler(option) {
        if (option !== undefined) this.lastSelectedOption = option;
      },
    },
    filter(enabled) {
      if (!enabled) this.filterValue = null;
    },
    focusBoundaryActive() {
      this.fieldFocusBoundary.schedule();
    },
  },
  beforeUnmount() {
    this.unbindDialogBodyOverlayScrollCoordinator();
    this.fieldFocusBoundary.dispose();
  },
  methods: {
    alignOverlay() {
      if (!this.dialogBodyOverlayScrollCoordinator.align()) {
        return alignPickerOverlay(this.overlay, this.$el, () =>
          PrimeVueSelect.methods.alignOverlay.call(this),
        );
      }
    },
    onFocus(event) {
      this.fieldFocusBoundary.focus();
      return PrimeVueSelect.methods.onFocus.call(this, event);
    },
    onBlur(event) {
      // Keep the focus ring local even when validation includes sibling controls.
      this.focused = false;
      this.fieldFocusBoundary.blur(event);
    },
    onKeyDown(event) {
      this.$emit('keydown', event);
      if (!event.defaultPrevented) return PrimeVueSelect.methods.onKeyDown.call(this, event);
    },
    onEnterKey(event) {
      if (this.enterQueryHandler?.(event)) return;
      if (!this.keepOpenOnSelect || !this.overlayVisible || this.focusedOptionIndex === -1) {
        return PrimeVueSelect.methods.onEnterKey.call(this, event);
      }
      this.onOptionSelect(event, this.visibleOptions[this.focusedOptionIndex]);
      event.preventDefault();
    },
    singleSelectableSuggestion() {
      const options = this.visibleOptions.filter((option) => this.isValidOption(option));
      return options.length === 1 ? options[0] : null;
    },
    onTabKey(event, pressedInInputText = false) {
      if (event.shiftKey && this.shiftTabSelect?.(event)) return;
      if (!event.shiftKey && this.overlayVisible && this.tabExitHandler?.(event)) return;
      if (!event.shiftKey && this.overlayVisible && this.singleResultTabHandler) {
        const option = this.singleSelectableSuggestion();
        if (option !== null && this.singleResultTabHandler(event, option)) return;
      }
      return PrimeVueSelect.methods.onTabKey.call(this, event, pressedInInputText);
    },
    onOverlayClick(event) {
      const result = PrimeVueSelect.methods.onOverlayClick.call(this, event);
      // Disabled rows and headings remain inside the logical field.
      if (document.activeElement === document.body) {
        this.$refs.focusInput?.focus({ preventScroll: true });
      }
      return result;
    },
    onClearClick(event) {
      this.$refs.focusInput?.focus();
      this.lastSelectedOption = undefined;
      const result = PrimeVueSelect.methods.onClearClick.call(this, event);
      if (this.overlayVisible) this.hide(true);
      this.$emit('clear');
      return result;
    },
    onOptionSelect(event, option, isHide = true) {
      if (!this.overlayVisible || !this.isValidOption(option)) return;
      const keepOpen = this.keepOpenOnSelect?.(option) === true;
      if (keepOpen) {
        this.focusedOptionIndex = -1;
        // PrimeVue selects on mousedown. A navigation choice can replace the
        // pressed row before click, so preserve focus without relying on click.
        event?.preventDefault?.();
      }
      this.lastSelectedOption = option;
      PrimeVueSelect.methods.onOptionSelect.call(this, event, option, isHide && !keepOpen);
      this.$emit('option-select', { originalEvent: event, value: option });
      if (keepOpen) {
        this.$nextTick(() => {
          if (this.overlayVisible && document.activeElement === document.body) {
            this.$refs.focusInput?.focus({ preventScroll: true });
          }
        });
      }
      return keepOpen;
    },
    selectOptionAndClose(event, option) {
      if (!this.onOptionSelect(event, option, false)) this.hide(false);
    },
    onFilterChange(event) {
      this.filterValue = event.target.value;
      this.focusedOptionIndex = -1;
      this.$emit('filter', { originalEvent: event, value: this.filterValue });
      if (this.virtualScroller?.element) this.virtualScroller.scrollToIndex(0);
    },
    commitFormFieldBlur(event, isCurrent) {
      this.focused = false;
      this.focusedOptionIndex = -1;
      this.searchValue = '';
      this.$emit('blur', event);
      this.$nextTick(() => {
        if (!isCurrent()) return;
        this.formField.onChange?.({ originalEvent: event, value: this.d_value });
        this.$nextTick(() => {
          if (isCurrent()) this.formField.onBlur?.(event);
        });
      });
    },
    bindDialogBodyOverlayScrollCoordinator() {
      this.dialogBodyOverlayScrollCoordinator.bind({
        alignOverlay: () => PrimeVueSelect.methods.alignOverlay.call(this),
        appendTo: this.appendTo,
        control: this.$el,
        overlay: this.overlay,
        isVisible: () => this.overlayVisible,
      });
    },
    unbindDialogBodyOverlayScrollCoordinator() {
      this.dialogBodyOverlayScrollCoordinator?.unbind();
    },
    onOverlayAfterEnter() {
      const result = PrimeVueSelect.methods.onOverlayAfterEnter.call(this);
      this.bindDialogBodyOverlayScrollCoordinator();
      return result;
    },
    onOverlayLeave(el) {
      this.fieldFocusBoundary.schedule();
      this.unbindDialogBodyOverlayScrollCoordinator();
      return PrimeVueSelect.methods.onOverlayLeave.call(this, el);
    },
    onOverlayAfterLeave(el) {
      const result = PrimeVueSelect.methods.onOverlayAfterLeave.call(this, el);
      this.fieldFocusBoundary.schedule();
      return result;
    },
  },
};
